"use client";

import type React from "react";
import { useId } from "react";
import type { SelectOption } from "shared/SelectOption";
import FormControl, {
  type FormControlMessageProps,
  type FormControlProps,
} from "src/components/form/FormControl/FormControl";
import Label from "src/components/form/Label/Label";
import Select, { type SelectProps } from "src/components/form/Select/Select";

type FieldOwnedProps =
  | "className"
  | "defaultValue"
  | "disabled"
  | "error"
  | "id"
  | "name"
  | "onValueProcess"
  | "required"
  | "value";

export type SelectFieldProps<O extends SelectOption> = Omit<FormControlProps, "children"> &
  Omit<SelectProps<O>, FieldOwnedProps | "ref"> & {
    controlClassName?: string;
    hideLabel?: boolean;
    inputRef?: SelectProps<O>["ref"];
    message?: React.ReactNode;
    messageProps?: FormControlMessageProps;
  };

const SelectField = <O extends SelectOption>(props: SelectFieldProps<O>) => {
  const {
    name,
    id,
    className,
    controlClassName,
    defaultValue,
    value,
    disabled,
    hideLabel,
    onValueProcess,
    label,
    error,
    validate,
    inputRef,
    required,
    message,
    messageProps,
    ...selectProps
  } = props;
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const showLabel = !hideLabel && Boolean(label);

  return (
    <FormControl
      name={name}
      id={controlId}
      className={className}
      defaultValue={defaultValue}
      value={value}
      disabled={disabled}
      onValueProcess={onValueProcess}
      label={label}
      error={error}
      validate={validate}
      required={required}
    >
      {showLabel ? <Label id={`${controlId}-label`} /> : null}
      <FormControl.Input>
        <Select<O>
          {...selectProps}
          ref={inputRef}
          className={controlClassName}
          required={required}
          aria-labelledby={
            selectProps["aria-labelledby"] ?? (showLabel ? `${controlId}-label` : undefined)
          }
          aria-label={selectProps["aria-label"] ?? (typeof label === "string" ? label : undefined)}
        />
      </FormControl.Input>
      <FormControl.Message {...messageProps}>{message}</FormControl.Message>
    </FormControl>
  );
};

export default SelectField;
